import { useContext } from 'react'
import {
  AltruistOpenRothContext,
  AltruistOpenRothType,
} from '../AltruistOpenRothContext'
import { Button, Card, Grid, TextField } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import WestIcon from '@mui/icons-material/West'
import '../AltruistOpenRoth.css'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { IntakeContext } from 'context/IntakeContext'

import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

export default function ReviewCard() {
  const { setCurrModalCard, setModalOpen, emailAltruist, setEmailAltruist } =
    useContext(AltruistOpenRothContext)
  const { intakeState, updateIntakeStateAtKey, updateUser, currentErrors } =
    useContext(IntakeContext)
  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const handleClose = () => {
    setModalOpen(false)
    setCurrModalCard(0)
  }

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <div className="cancelIconContainer" onClick={() => handleClose()}>
        <CancelIcon className="cancelIcon" fontSize="large" />
      </div>
      <div className="banner" />
      <div className="cardContentContainer">
        <Button
          variant="gradient"
          color="dark"
          sx={{
            height: 'auto',
            position: 'absolute',
            top: '150px',
            right: '0',
          }}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
        <div>
          <div className="contentHeader">
            Let's Review: Confirm Your Details
          </div>
          <div className="contentDetailText">
            Let's make sure everything looks correct. Please make any
            adjustments and press 'Submit' when you are ready!
          </div>
        </div>
        <div className="">
          <Grid
            container
            columnSpacing={3}
            rowSpacing={2}
            mt={1}
            sx={{ textAlign: 'left' }}
          >
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'firstName',
                label: 'First Name',
                placeholder: 'First Name',
                inputType: 'text',
                defaultErrorState: currentErrors[intakeState.firstName],
                value: intakeState.firstName,
                onChange: updateIntakeStateAndSubmit((firstName) =>
                  updateIntakeStateAtKey({ firstName })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'lastName',
                label: 'Last Name',
                placeholder: 'Last Name',
                inputType: 'text',
                defaultErrorState: currentErrors[intakeState.lastName],
                value: intakeState.lastName,
                onChange: updateIntakeStateAndSubmit((lastName) =>
                  updateIntakeStateAtKey({ lastName })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'email',
                label: 'Email',
                placeholder: 'Email',
                inputType: 'text',
                value: emailAltruist,
                onChange: (e) => setEmailAltruist(e),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'phone',
                label: 'phone number',
                placeholder: 'Eg. xxx-xxx-xxxx',
                inputType: 'tel',
                value: intakeState.phone,
                onChange: updateIntakeStateAndSubmit((phone) =>
                  updateIntakeStateAtKey({ phone })
                ),
                defaultErrorState: currentErrors.phone,
              })}
            </Grid>
          </Grid>
          <Grid
            container
            columnSpacing={3}
            rowSpacing={2}
            mt={1}
            sx={{ textAlign: 'left' }}
          >
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'address',
                label: 'Street Address',
                placeholder: '12345 Main Street',
                inputType: 'text',
                value: intakeState.address,
                onChange: updateIntakeStateAndSubmit((address) =>
                  updateIntakeStateAtKey({ address })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'address2',
                label: 'Apt, Suite, Bldg. (optional)',
                placeholder: '',
                inputType: 'text',
                value: intakeState.address2,
                onChange: updateIntakeStateAndSubmit((address2) =>
                  updateIntakeStateAtKey({ address2 })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderMuiFormField({
                id: 'city',
                label: 'City',
                placeholder: 'City',
                inputType: 'text',
                value: intakeState.city,
                onChange: updateIntakeStateAndSubmit((city) =>
                  updateIntakeStateAtKey({ city })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderMuiFormField({
                id: 'state',
                label: 'State',
                placeholder: 'State',
                inputType: 'text',
                value: intakeState.state,
                onChange: updateIntakeStateAndSubmit((state) =>
                  updateIntakeStateAtKey({ state })
                ),
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {renderMuiFormField({
                id: 'zipcode',
                label: 'Zipcode',
                placeholder: '#####',
                inputType: 'zipcode',
                value: intakeState.zipcode,
                onChange: updateIntakeStateAndSubmit((zipcode) =>
                  updateIntakeStateAtKey({ zipcode })
                ),
              })}
            </Grid>
          </Grid>
        </div>
        <div className="modalNavigation">
          <div className="backButtonContainer">
            <Button
              variant="text"
              size="large"
              onClick={() => setCurrModalCard((prev) => prev - 1)}
              startIcon={<WestIcon />}
            >
              back
            </Button>
          </div>
          <div className="greenButtonContainer">
            <Button onClick={() => setCurrModalCard((prev) => prev + 1)}>
              SUBMIT
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}
